/* eslint-disable @typescript-eslint/naming-convention */
import React, { useEffect, useState, FunctionComponent } from 'react';

import { useFetch } from '@api/ApiHelper';
import { PreviewType } from '@api/v4/attachments/attachmentTypes';
import { IssuesResponse } from '@api/v4/private/assets/issues';
import AssetAttachment, { FullAttachment } from '@components/asset/modal/tabs/asset_details/asset_attachments/AssetAttachment';

import './styles/_asset_attachments_list.scss';

interface AttachmentConversionInfo {
  attachmentKey: string;
  conversionOptions: string[];
  colorSpace: string;
  isConvertable: boolean;
  presetSizes: number[];
  previewType: PreviewType;
  processed: boolean;
  restrictedDimensions: {
    width: { min: number; max: number };
    height: { min: number; max: number };
  };
  thumbnailUrl: string;
}

export interface AssetAttachmentsProps {
  assetCreatedAt: string;
  assetKey: string;
  attachmentConversionInfo: AttachmentConversionInfo[];
  attachments: FullAttachment[];
  downloadRequestId: number | null;
  isDownloadable: boolean;
  isProcessing: boolean;
  manifestDigestPresent: boolean;
  viewOnly: boolean | null;
}

const AssetAttachments: FunctionComponent<AssetAttachmentsProps> = ({
  assetCreatedAt,
  assetKey,
  attachmentConversionInfo,
  attachments,
  downloadRequestId,
  isDownloadable,
  isProcessing,
  viewOnly
}) => {
  const [selectedAttachmentKey, setSelectedAttachmentKey] = useState(attachments[0].attachment_key);
  const fetchIssues = useFetch<IssuesResponse>({
    url: `/api/v4/private/assets/${assetKey}/issues`
  });

  useEffect(() => {
    BF.fx.dispatchWindowEvent('updateSelectedAttachment', null, { attachmentKey: selectedAttachmentKey });
  }, [selectedAttachmentKey]);

  // This check is because some older (pre 2021) assets are stuck in 'processing' state
  // when they have all conversion info (height, width etc.)
  // so the conversion options shouldn't be hidden for these
  // whereas newer assets in 'processing' most likely don't have conversion info so the conversion
  // options SHOULD be hidden in this case
  const isOld = (new Date(assetCreatedAt) < new Date('1/1/2021'));
  const displayAsProcessed = !isProcessing || (isProcessing && isOld);

  const attachmentChildren = attachments.map((attachment) => {
    const {
      conversionOptions,
      colorSpace,
      isConvertable,
      presetSizes,
      previewType,
      processed,
      restrictedDimensions,
      thumbnailUrl
    } = attachmentConversionInfo.find((att) => (
      att.attachmentKey === attachment.attachment_key
    ));
    return (
      <AssetAttachment
        key={attachment.attachment_key}
        assetKey={assetKey}
        // eslint-disable-next-line @typescript-eslint/naming-convention
        attachment={{ ...attachment, thumbnail_url: thumbnailUrl }}
        attachmentConversionOptions={conversionOptions}
        colorSpace={colorSpace}
        displayAsProcessed={displayAsProcessed}
        downloadRequestId={downloadRequestId}
        isConvertable={isConvertable}
        isDownloadable={isDownloadable}
        issues={fetchIssues}
        presetSizes={presetSizes}
        previewType={previewType}
        processed={processed}
        restrictedDimensions={restrictedDimensions}
        selectedAttachmentKey={selectedAttachmentKey}
        setSelectedAttachmentKey={setSelectedAttachmentKey}
        viewOnly={viewOnly}
      />
    );
  });

  return (
    <div className="asset-attachments__list">
      <div className="asset-attachments__list--title__container">
        <div className="asset-attachments__list--title">
          <span className="bff-paperclip" />
          <h4
            className="asset-attachments__list--title-count"
            data-testid="asset-attachments--title-count"
          >
            {attachments.length}
          </h4>
          <h4>{attachments.length !== 1 ? bfTranslate('attachments') : bfTranslate('attachment')}</h4>
        </div>
        {!displayAsProcessed && (
          <div className="asset-attachments__list--title" data-testid="asset-attachments--processing">
            <img
              alt={bfTranslate('Processing indicator')}
              aria-hidden
              src="https://cdn.bfldr.com/27C9EC93/at/jgp7255xw8jcg79x68pk9pqt/processing"
            />
            <span className="asset-attachments__list--title-processing-label">{bfTranslate('Processing')}</span>
          </div>
        )}
      </div>
      {attachmentChildren}
    </div>
  );
};

export default AssetAttachments;
